/**
 * Invoice Checker - Main App
 * ==========================
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { config } from './utils/config';

// Pages
import Dashboard from './pages/Dashboard';
import InvoiceDashboard from './pages/InvoiceDashboard';
import RFQInvoices from './pages/RFQInvoices';
import BulkUpload from './pages/BulkUpload';
import InvoiceReconciliation from './pages/InvoiceReconciliation';
import QuotesContracts from './pages/QuotesContracts';
import ComparisonPage from './pages/MatchComparison';
import InvoiceDetails from './pages/InvoiceDetails';

// Global styles
import './App.scss';

const AppContent: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth();

    // Show loading while checking auth
    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    // Not authenticated - show message with redirect option
    if (!isAuthenticated) {
        return (
            <div className="loading-container">
                <div className="error-card">
                    <h2>Authentication Required</h2>
                    <p>Please login via the Portal to access Invoice Checker.</p>
                    <button
                        className="btn-login"
                        onClick={() => {
                            const currentUrl = window.location.origin;
                            window.location.href = `${config.PORTAL_URL}/signin?redirect=${encodeURIComponent(currentUrl)}`;
                        }}
                    >
                        Go to Portal Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/rfq-invoices" element={<RFQInvoices />} />
            <Route path="/invoice-details" element={<InvoiceDetails />} />
            <Route path="/bulk-upload" element={<BulkUpload />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/reconciliation" element={<InvoiceReconciliation />} />
            <Route path="/reconciliation/:id" element={<ComparisonPage />} />
            <Route path="/quotes-contracts" element={<QuotesContracts />} />
            <Route path="/settings" element={<Dashboard />} /> {/* Placeholder */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthProvider>
                <SocketProvider>
                    <AppContent />
                </SocketProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
